import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { addImportExportDetailsToDocumentSummary } from '../../../../services/allInvoiceApi';

pdfMake.vfs = pdfFonts?.pdfMake?.vfs;

const SectionDetails = (section, data) => {
  const result = data
    .filter(
      (item) =>
        item.short_desc.startsWith(section) ||
        (section === 'Invoice' && item?.short_desc?.startsWith('Currency')),
    )
    .map((item) => [
      { text: item?.short_desc ? `${item.short_desc}:-` : 'N/A', bold: true },
      { text: item?.value || 'N/A' },
    ]);

  return result.length === 0 ? [{}] : result;
};

const getPaymentDetails = (labelPrefix, data) => {
  // Filter labels based on prefix
  const filteredLabels = data
    .filter((item) => item.short_desc.startsWith(labelPrefix))
    .map((item) => item.short_desc);

  // Header row definition
  const headerRow = filteredLabels.map((label) => ({
    text: label,
    style: 'tableHeader',
  }));

  // Data rows definition
  const dataRows = [
    filteredLabels.map((label) => ({
      text: data.find((item) => item.short_desc === label)?.value || 'N/A',
      style: 'tableCell',
      alignment: 'center',
    })),
  ];

  // Return both header row and data rows
  return [headerRow, ...dataRows];
};

const generatePdf = async (data, filename, documentType, id) => {
  // Extract filename without extension and replace underscores with hyphens
  let nameWithoutExtension = filename?.split('.').slice(0, -1).join('.');
  nameWithoutExtension = nameWithoutExtension?.replace(/_/g, '-');

  // Define your document template
  const documentDefinition = {
    content: [
      {
        text: documentType === 'invoice' ? 'Invoice' : 'Receipt', // Title based on document type
        style: 'header',
      },
      {
        columns: [
          {
            width: '*',
            text:
              documentType === 'invoice'
                ? 'Invoice Details'
                : 'Receipt Details', // Section title
            style: 'sectionTitle',
          },
        ],
      },
      {
        table: {
          body:
            documentType === 'invoice'
              ? SectionDetails('Invoice', data)
              : SectionDetails('Receipt', data),
        },
        layout: {
          hLineWidth: () => 0, // Fixed horizontal line width
          vLineWidth: () => 0, // No vertical lines
          paddingTop: () => 5, // Fixed top padding
          paddingBottom: () => 5, // Fixed bottom padding
        },
        margin: [0, 10, 0, 20], // Adjust margins
      },
      {
        columns: [
          {
            width: '*',
            text: 'Seller Details', // Section title
            style: 'sectionTitle',
          },
          {
            width: '*',
            text: 'Client Details', // Section title
            style: 'sectionTitle',
          },
        ],
      },
      {
        columns: [
          {
            width: '*',
            table: {
              body: SectionDetails('Supplier', data),
            },
            layout: {
              hLineWidth: () => 0, // Fixed horizontal line width
              vLineWidth: () => 0, // No vertical lines
              paddingTop: () => 5, // Fixed top padding
              paddingBottom: () => 5, // Fixed bottom padding
            },
            margin: [0, 10, 10, 20], // Adjust margins
          },
          {
            width: '*',
            table: {
              widths: ['auto', '*'], // Adjusted widths for the table
              body: SectionDetails('Customer', data),
            },
            layout: {
              hLineWidth: () => 0, // Fixed horizontal line width
              vLineWidth: () => 0, // No vertical lines
              paddingTop: () => 5, // Fixed top padding
              paddingBottom: () => 5, // Fixed bottom padding
            },
            margin: [0, 10, 0, 20], // Adjust margins
          },
        ],
      },
      {
        text: 'Items', // Section title
        style: 'sectionTitle',
        margin: [0, 10, 0, 10], // Adjust margins
      },
      {
        table: {
          headerRows: 1,
          widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'],
          body: [
            [
              { text: 'No.', style: 'tableHeader' },
              { text: 'Description', style: 'tableHeader' },
              { text: 'Qty', style: 'tableHeader' },
              { text: 'Unit', style: 'tableHeader' },
              { text: 'Net Price', style: 'tableHeader' },
              { text: 'Tax', style: 'tableHeader' },
              { text: 'Total', style: 'tableHeader' },
            ],
            ...Object.values(
              data
                .filter((item) => item.section === 'ItemDetails')
                .reduce((acc, item) => {
                  if (!acc[item.product]) {
                    acc[item.product] = { product: item.product };
                  }
                  acc[item.product][item.label] = item.value;
                  return acc;
                }, {}),
            ).map((item, index) => [
              { text: (index + 1).toString(), style: 'tableCell' },
              { text: item.Description || 'N/A', style: 'tableCell' },
              { text: item.Qty || 'N/A', style: 'tableCell' },
              { text: item.UnitOfMeasure || 'N/A', style: 'tableCell' },
              { text: item.NetPrice || 'N/A', style: 'tableCell' },
              { text: item.Tax || 'N/A', style: 'tableCell' },
              { text: item.Total_for_each_item || 'N/A', style: 'tableCell' },
            ]),
          ],
        },
        layout: 'lightHorizontalLines', // Table layout
        margin: [0, 0, 0, 20], // Adjust margins
      },
      {
        columns: [
          {
            width: '*',
            text: 'Summary', // Section title
            style: 'sectionTitle',
          },
        ],
      },
      {
        table: {
          headerRows: 1,
          widths: '*',
          body: getPaymentDetails('Payment', data),
        },
        layout: 'lightHorizontalLines', // Table layout
        margin: [0, 10, 0, 20], // Adjust margins
      },
    ],
    styles: {
      header: {
        fontSize: 22,
        bold: true,
        alignment: 'center',
        margin: [0, 0, 0, 20], // Adjust top margin
      },
      sectionTitle: {
        fontSize: 16,
        bold: true,
        margin: [0, 10, 0, 5], // Adjust margins
      },
      tableHeader: {
        bold: true,
        fontSize: 12,
        fillColor: '#eeeeee',
        alignment: 'center',
        margin: [0, 5],
      },
      tableCell: {
        fontSize: 12,
        margin: [0, 5],
      },
    },
  };

  // Create PDF and initiate download
  pdfMake.createPdf(documentDefinition).download(`${nameWithoutExtension}.pdf`);
  await addImportExportDetailsToDocumentSummary(
    id,
    `${nameWithoutExtension}.pdf`,
  );
};

export default generatePdf;
